import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIdleAutoHideProps {
  timeoutMs?: number;
  pauseTimer?: boolean;
}

/**
 * Hook to auto-hide a component after a period of mouse inactivity.
 * Visibility is reset on mouse movement.
 * set pauseTimer to true to temporarily disable the auto-hide timer.
 * @param timeoutMs
 * @param pauseTimer
 * @return { isVisible, setIsVisible }
 */
export const useIdleAutoHideOnMouseMove = ({
  timeoutMs = 1500,
  pauseTimer = false,
}: UseIdleAutoHideProps): { isVisible: boolean; setIsVisible: (state: boolean) => void } => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Logic to start the timer
   */
  const startTimer = useCallback(() => {
    clearTimeout(hideTimerRef.current!);
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, timeoutMs);
  }, [timeoutMs]);

  /**
   * Sync timer when pauseTimer changes
   */
  useEffect(() => {
    if (!pauseTimer && isVisible) {
      startTimer();
    } else if (pauseTimer) {
      clearTimeout(hideTimerRef.current!);
    }
  }, [isVisible, pauseTimer, startTimer]);

  /**
   * Event Listener to handles "Waking Up"
   */
  useEffect(() => {
    const handleMouseMove = () => {
      setIsVisible(true);

      if (!pauseTimer) {
        startTimer();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(hideTimerRef.current!);
    };
  }, [pauseTimer, startTimer]); // Re-run effect when pause state changes

  return { isVisible, setIsVisible };
};
